import { Button, Checkbox, Group, Modal, Stack, Text } from "@mantine/core";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { thunkBuildRoutingGraphFromStandardRoomPipeGeometries } from "../DesignTab/routing/pipeThunks";
import {
  selectPipeSystemsCopyModalOpen,
  setPipeSystemsCopyModalOpenInRedux,
} from "../DesignTab/designSlice";
import { selectHttpUniquePipeAndDuctSystemNamesInStandardSpace } from "./pipeSystemSelectors";

const PipeSystemsCopyModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const opened = useAppSelector(selectPipeSystemsCopyModalOpen);
  const systems = useAppSelector(selectHttpUniquePipeAndDuctSystemNamesInStandardSpace);
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);

  const hasSystems = systems.length > 0;
  const selectedCount = selectedSystems.length;

  const selectedSet = useMemo(() => new Set(selectedSystems), [selectedSystems]);

  useEffect(() => {
    if (opened) {
      setSelectedSystems(systems);
    }
  }, [opened, systems]);

  const closeModal = () => {
    dispatch(setPipeSystemsCopyModalOpenInRedux(false));
  };

  const toggleSystem = (system: string) => {
    setSelectedSystems(
      selectedSystems.includes(system)
        ? selectedSystems.filter((s) => s !== system)
        : [...selectedSystems, system]
    );
  };

  const handleConfirm = () => {
    dispatch(thunkBuildRoutingGraphFromStandardRoomPipeGeometries(selectedSystems));
    closeModal();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={closeModal}
        title="Velg systemer som skal kopieres fra standardrom"
        centered
      >
        {!hasSystems ? (
          <Text size="sm">
            Ingen systemer for rør/kanaler ble funnet i standardrommet (IfcPipeSegment eller
            IfcDuctSegment).
          </Text>
        ) : (
          <Stack gap="xs">
            <Text size="sm">
              Marker systemene du vil kopiere rør og kanaler fra i standardrommet.
            </Text>

            <Checkbox
              label="Velg alle"
              checked={selectedCount === systems.length}
              indeterminate={selectedCount > 0 && selectedCount < systems.length}
              onChange={(event) => setSelectedSystems(event.currentTarget.checked ? systems : [])}
            />

            <Stack gap={4}>
              {systems.map((system) => (
                <Checkbox
                  key={system}
                  label={system}
                  checked={selectedSet.has(system)}
                  onChange={() => toggleSystem(system)}
                />
              ))}
            </Stack>
          </Stack>
        )}

        <Group justify="flex-end" mt="lg">
          <Button variant="light" onClick={closeModal}>
            Avbryt
          </Button>
          <Button onClick={handleConfirm} disabled={!hasSystems || selectedCount === 0}>
            Bekreft
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default PipeSystemsCopyModal;
